/**
 * ShipmentsTable.js
 *
 * Full-featured shipments table.
 * Unique responsibilities vs PartialShipmentsTab:
 *  - Bulk invoice creation & miles refresh
 *  - Portal-rendered action dropdown (avoids stacking-context clipping)
 *  - Auto-grow / resizable columns + expanded-cell-on-hover
 *  - Advanced filters persisted in Redux (survives tab switches)
 */

import React, { useState, useEffect, useRef, useCallback } from "react";
import ReactDOM from "react-dom";
import httpClient from "../../../../api/httpClient";
import Table from "../../../../components/ACS-Dashboard/ACS-Blocks/Table";
import Loader from "../../../../components/ACS-Dashboard/ACS-Blocks/Loader";
import useExpandedRowHover from "../../../../components/ACS-Dashboard/ACS-Blocks/useExpandedRowHover";
import { ChevronDown, ChevronRight, GitBranch, MoreHorizontal } from "lucide-react";
import ActionMenu from "../../../../components/ACS-Dashboard/ACS-Blocks/ActionMenu";
import Cookies from "js-cookie";
import { useSelector, useDispatch } from "react-redux";
import { addTab, setListViewState, setStatusFilter } from "../../../../redux/slices/shipmentSlice";
import { usePrivilege } from "../../../../guard/usePrivilege";
import { isCarrierOrAgentOrg } from "../../../../guard/privilegeUtils";
import GlobalPagination from "../../../../components/ACS-Dashboard/ACS-Blocks/GlobalPagination";
import { addTab as addInvoiceTab, setActiveTab as setActiveInvoiceTab } from "../../../../redux/slices/invoiceSlice";
import { useNavigate } from "react-router-dom";
import { fallbackName } from "../../Invoice/Utils/util";
import { SORTABLE_COLUMNS, FILTER_TABS } from "./Utils/sortableColumns";
import notify from "../../../../components/ACS-Dashboard/ACS-Blocks/Toast";
import { refreshRatesAndUpdateCharges } from "./ViewShipment/rateRefresh";
import { useShipmentStatuses } from "./Utils/shipmentStatuses";


import {
  TABLE_HEADERS,
  DISABLED_STATUSES,
  makeKey,
  buildShipmentSearchPayload,
  formatShipmentRow,
  useShipmentFilters,
  AdvancedFilterToolbar,
  AdvancedFilterModal,
  DEFAULT_ADVANCED_FILTERS,
  DEFAULT_QUICK_FILTER,
  filtersOverrideStatus,
  isRefreshBlockedForShipment,

} from "./shipmentShared";
import {
  useTableSavedViews,
  TableSavedViewsControl,
} from "../../../TableSavedViews";
import {
  ShipmentViewTabsControl,
  useShipmentViewTabsContext,
} from "../ViewTabs";

import { setAdvancedFilters } from "../../../../redux/slices/shipmentSlice";

// ─── Layout constants (unique to this view's resizable/expandable cell setup) ──

const DATA_COLUMN_MIN_WIDTH    = 200;
const DATA_COLUMN_MAX_WIDTH    = 420;
const SELECTION_COLUMN_WIDTH   = 80;
const ACTION_COLUMN_WIDTH      = 50;
const SHIPMENT_NUMBER_HEADER   = "Shipment Number";

// ─── Main Component ───────────────────────────────────────────────────────────

const ShipmentsTable = ({
  searchQuery,
  filter,
  searchTrigger,
  setTotalShipments,
  stateKey = "shipment-list",
  showAdvancedFilters = true,
  isSearchTab = false,
  // Set when this table is a saved "view tab": its stored config is the layout the
  // tab opens with, and the Tabs control targets it for updates.
  viewTab = null,
  /**
   * Baseline layout for a tab that is itself a saved thing — a pinned view tab's
   * config, or the status/advanced filter an overview widget opened with. Also what
   * "System default" falls back to, instead of the bare system one.
   */
  initialConfig = null,
}) => {
  const dispatch = useDispatch();
  const { listViewState = {}, advancedFilters: advancedFiltersByTab = {} } = useSelector(
    (s) => s.shipments
  );
  const savedViewState = listViewState[stateKey] || {};
  // What the store knew about this tab as this mount began: where the user left it
  // earlier in the session, before anything here writes to it.
  const sessionStateOnMountRef = useRef(savedViewState);
  // Likewise for its rule-based filters, which live in their own slice of state and
  // are not part of savedViewState.
  const sessionFiltersOnMountRef = useRef(advancedFiltersByTab[stateKey]);

  const [shipments,          setShipments]         = useState([]);
  const [isLoading,          setIsLoading]          = useState(false);
  const [selectedShipments,  setSelectedShipments]  = useState([]);
  const [currentPage,        setCurrentPage]        = useState(savedViewState.currentPage   || 1);
  const [shipmentsPerPage,   setShipmentsPerPage]   = useState(savedViewState.shipmentsPerPage || 50);
  const [openDropdownRow,    setOpenDropdownRow]    = useState(null);
  const [dropdownPos,        setDropdownPos]        = useState({ top: 0, left: 0 });
  const [totalPages,         setTotalPages]         = useState(1);
  // Kept here as well as reported upward: the pagination bar shows which rows the
  // page covers, which needs the count and not just the number of pages.
  const [totalItems,         setTotalItems]         = useState(0);
  const [showBulkMenu,       setShowBulkMenu]       = useState(false);
  const [isRefreshingMiles,  setIsRefreshingMiles]  = useState(false);
  const [isRefreshingRates,  setIsRefreshingRates]  = useState(false);
  const [showFilterModal,    setShowFilterModal]    = useState(false);
  const [showLoadingOverlay, setShowLoadingOverlay] = useState(false);
  const [expandedParents,    setExpandedParents]    = useState(() => new Set());
  const [childrenByParent,   setChildrenByParent]   = useState({});
  const [loadingChildren,    setLoadingChildren]    = useState(() => new Set());

  // Hovering any cell wraps the whole row, so long values in every column are readable at once.
  const { expandedRowKey, getRowHoverProps } = useExpandedRowHover();

  const [sortBy,    setSortBy]    = useState(savedViewState.sortBy    || null);
  const [ascending, setAscending] = useState(savedViewState.ascending ?? false);

  const dropdownRef       = useRef(null);
  const checkboxHeaderRef = useRef(null);
  const bulkMenuRef       = useRef(null);
  const tableContainerRef = useRef(null);
  const prevFilterRef     = useRef(filter);
  const prevSearchTrigRef = useRef(searchTrigger);

  const getBulkMenuStyle = () => {
    const container = tableContainerRef.current;
    const header = checkboxHeaderRef.current;
    if (!container || !header) return null;

    const checkbox = header.querySelector('input[type="checkbox"]');
    const anchor = checkbox ?? header;
    const anchorRect = anchor.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();

    return {
      top:
        anchorRect.bottom - containerRect.top + container.scrollTop + 6,
      // Left-align on the checkbox itself (not centered on its narrow width) -- centering put
      // the menu's left half directly over the checkbox, looking like it overlapped it.
      left:
        anchorRect.left -
        containerRect.left +
        container.scrollLeft,
    };
  };

  const token      = Cookies.get("authToken");
  const officeCode = Cookies.get("officeCodes");
  const navigate   = useNavigate();

  const { defaultTabs, dynamicTabs } = useSelector((s) => s.invoices);
  const { hasPrivilege }  = usePrivilege();
  const canCreateInvoice  = hasPrivilege("invoices:CREATE");

  // LGX-1164: the Advanced Filter's "Status" field must offer the same statuses the
  // caller can actually see elsewhere (the backend already filters this per caller —
  // a shipper gets every status, a carrier only the ones meaningful to them). The
  // rule field's options are upper-cased (e.g. "IN TRANSIT"), unlike the API's own
  // casing, so match that here.
  const { apiStatuses: rawStatusOptions } = useShipmentStatuses();
  const advancedFilterStatusOptions = rawStatusOptions.map((s) => s.toUpperCase());

  // Refresh Miles/Refresh Rates: Shipment Admin only -- not every shipper user, and no
  // carrier user regardless of privilege. isAdminUser (shipments:ADMIN_VIEW) alone isn't
  // org-scoped, so isShipperOrg is what excludes carriers here -- same combined-gate pattern
  // ShipmentDetailsView.js already uses for its own admin-only actions.
  const isAdminUser = hasPrivilege("shipments:ADMIN_VIEW");
  let isShipperOrg = false;
  try {
    const storedOrgTypes = JSON.parse(localStorage.getItem("organizationTypes") || "[]");
    const normalizedOrgTypes = (Array.isArray(storedOrgTypes) ? storedOrgTypes : [])
      .map((t) => (t ?? "").toString().toLowerCase().trim())
      .filter(Boolean);
    isShipperOrg = normalizedOrgTypes.length === 1 && normalizedOrgTypes[0] === "shipper";
  } catch {
    isShipperOrg = false;
  }
  const canRefreshMilesOrRates = isAdminUser && isShipperOrg;

  // Filters persisted in Redux so they survive tab switching
  const { filters: advancedFilters, updateFilters, clearFilters, removePill, activeCount } =
    useShipmentFilters({ stateKey, persist: true });

  const prevQuickFilterForDirtyRef = useRef(filter);
  const prevAdvancedFiltersForDirtyRef = useRef(JSON.stringify(advancedFilters));

  const viewTabs = useShipmentViewTabsContext();

  const tableViews = useTableSavedViews({
    pageKey: stateKey,
    allHeaders: TABLE_HEADERS,
    fixedPrefixCount: 2,
    defaultStickyColumnKeys: ["Selection", "Action", SHIPMENT_NUMBER_HEADER],
    initialConfig: initialConfig ?? viewTab?.config,
    getExtraConfig: () => ({
      sortBy,
      ascending,
      filter,
      advancedFilters,
      currentPage,
      pageSize: shipmentsPerPage,
    }),
    onApplyConfig: (config, _viewMeta, options = {}) => {
      // Coming back to the module mid-session, the store already holds the page,
      // filter and sort this tab was left on, and the view that gets re-applied on
      // every mount — a default one, or just the last one used — would put the user
      // back where the view starts instead. A fresh login has no session state to
      // keep (logout clears the store), so there the view still owns the first load.
      //
      // Only what is actually there is protected: a config carrying rule-based
      // filters is applied unless the session has that tab's filters too. Otherwise
      // a partly restored session — the tab marked visited with its filters missing —
      // would block its own filters from ever being applied, and the tab would list
      // every shipment.
      const session = sessionStateOnMountRef.current;
      const sessionCoversFilters =
        config.advancedFilters === undefined ||
        sessionFiltersOnMountRef.current !== undefined;
      if (options.isInitialLoad && session?.visited && sessionCoversFilters) return;

      if (config.sortBy !== undefined) setSortBy(config.sortBy);
      if (config.ascending !== undefined) setAscending(config.ascending);
      if (config.currentPage) setCurrentPage(config.currentPage);
      if (config.pageSize) setShipmentsPerPage(config.pageSize);
      if (config.filter !== undefined) {
        dispatch(setStatusFilter(config.filter));
        dispatch(setListViewState({ stateKey, filter: config.filter }));
      }
      if (config.advancedFilters !== undefined) {
        dispatch(setAdvancedFilters({
          stateKey,
          filters: config.advancedFilters ?? DEFAULT_ADVANCED_FILTERS,
        }));
      } else if (options.resetExtraConfig) {
        dispatch(setAdvancedFilters({ stateKey, filters: DEFAULT_ADVANCED_FILTERS }));
        dispatch(setStatusFilter(DEFAULT_QUICK_FILTER));
        dispatch(setListViewState({ stateKey, filter: DEFAULT_QUICK_FILTER }));
      }
    },
  });

  const {
    columnWidths: savedColumnWidths,
    setColumnWidths: onColumnResize,
    dataHeaders,
    controlLabel,
    views,
    activeView,
    loading: viewsLoading,
    error: viewsError,
    isDirty,
    selectView,
    saveNewView,
    updateActiveView,
    renameView,
    deleteView,
    setDefaultView,
    updateColumnVisibility,
    markDirty,
    reconcileDirtyState,
    captureConfig,
    // Views-driven column manager (full column set, hidden columns surface in the
    // "Hidden" section). Spread `columnManagerProps` onto <Table>; the rich panel
    // renders inside the single Views dropdown via `onManageColumns`.
    columnManagerProps,
    onManageColumns,
  } = tableViews;

  // Mark dirty only when the user changes filters, not when activeView updates after save.
  useEffect(() => {
    if (!activeView || viewsLoading) return;

    const quickFilterChanged = prevQuickFilterForDirtyRef.current !== filter;
    const advancedFiltersJson = JSON.stringify(advancedFilters);
    const advancedFiltersChanged =
      prevAdvancedFiltersForDirtyRef.current !== advancedFiltersJson;

    prevQuickFilterForDirtyRef.current = filter;
    prevAdvancedFiltersForDirtyRef.current = advancedFiltersJson;

    if (!quickFilterChanged && !advancedFiltersChanged) return;

    reconcileDirtyState();
  }, [activeView, filter, advancedFilters, viewsLoading, reconcileDirtyState]);

  // ── Loading overlay: only show after 250ms to avoid flash on fast requests ──
  useEffect(() => {
    if (!isLoading) { setShowLoadingOverlay(false); return; }
    const t = setTimeout(() => setShowLoadingOverlay(true), 250);
    return () => clearTimeout(t);
  }, [isLoading]);

  // ── Persist sort / page state to Redux (after saved views load) ──
  // Filter is owned by saved views (onApplyConfig) and explicit toolbar actions — do not
  // mirror the global statusFilter here; it defaults to "New" and would overwrite the view.
  //
  // Not gated on having a view selected: this is where the table picks its page back
  // up from when the module is remounted — leaving the module and coming back, say —
  // and most users are on the system default with no view of their own. Waiting for
  // views to load still matters, so the page the table opens on isn't overwritten by
  // the default 1 it renders with first.
  useEffect(() => {
    if (viewsLoading) return;
    dispatch(setListViewState({
      stateKey,
      currentPage,
      shipmentsPerPage,
      sortBy,
      ascending,
      // Marks the tab as one the user has actually been on this session, which is
      // what tells a later mount to keep this position over the view's.
      visited: true,
    }));
  }, [dispatch, stateKey, currentPage, shipmentsPerPage, sortBy, ascending, viewsLoading]);

  // ── Close action dropdown on outside click ──
  useEffect(() => {
    const handle = (e) => {
      if (e.target.closest("[data-action-trigger]")) return;
      if (dropdownRef.current && !dropdownRef.current.contains(e.target))
        setOpenDropdownRow(null);
    };
    document.addEventListener("mousedown", handle);
    return () => document.removeEventListener("mousedown", handle);
  }, []);

  // ── Close bulk menu on outside click ──
  useEffect(() => {
    const handle = (e) => {
      if (
        bulkMenuRef.current     && !bulkMenuRef.current.contains(e.target) &&
        checkboxHeaderRef.current && !checkboxHeaderRef.current.contains(e.target)
      ) setShowBulkMenu(false);
    };
    document.addEventListener("mousedown", handle);
    return () => document.removeEventListener("mousedown", handle);
  }, []);

  // ── Disabled rows (Cancelled shipments can't be actioned) ──
  const disabledByShipment = React.useMemo(() => {
    const m = new Map();
    for (const s of shipments)
      m.set(makeKey(s.shipmentNumber, s.officeCode), DISABLED_STATUSES.has((s.status || "").toUpperCase()));
    return m;
  }, [shipments]);


  const fetchShipments = useCallback(async (signal) => {
    try {
      setIsLoading(true);

      const payload = buildShipmentSearchPayload({
        searchQuery,
        filter,
        advancedFilters,
        sortBy,
        ascending,
        filterType: FILTER_TABS.ALL,
      });

      const { data } = await httpClient.post(
        `/api/shipment-details/partial-search?page=${currentPage - 1}&size=${shipmentsPerPage}`,
        payload,
        {
          signal,
          headers: {
            "Content-Type": "application/json",
            Authorization:  `Bearer ${token}`,
            "Office-Code":  officeCode,
          },
        }
      );

      if (signal?.aborted) return;

      setShipments(data.content ?? []);
      // Every shipment this tab's filter matches, across all its pages — the
      // toolbar shows it for whichever tab the user is on.
      setTotalShipments(data.totalElements);
      setTotalItems(data.totalElements ?? 0);
      setTotalPages(data.totalPages);

    } catch (err) {
      if (signal?.aborted || err?.code === "ERR_CANCELED") return;
      console.error("Error fetching shipments:", err);
      setShipments([]);
      setTotalShipments(0);
      setTotalItems(0);

      setTotalPages(1);
    } finally {
      if (!signal?.aborted) setIsLoading(false);
    }
  }, [
    currentPage, shipmentsPerPage, searchQuery, filter,
    sortBy, ascending, advancedFilters, token, officeCode, setTotalShipments,
  ]);

  useEffect(() => {
    if (viewsLoading) return;

    const filterChanged = prevFilterRef.current    !== filter;
    const searchChanged = prevSearchTrigRef.current !== searchTrigger;
    const needsReset    = (filterChanged || searchChanged) && currentPage !== 1;

    prevFilterRef.current    = filter;
    prevSearchTrigRef.current= searchTrigger;

    if (needsReset) { setCurrentPage(1); return; }

    const controller = new AbortController();
    fetchShipments(controller.signal);
    return () => controller.abort();
  }, [searchTrigger, filter, currentPage, fetchShipments, viewsLoading, activeView]);

  // ── Sorting ──
  const handleSort = (newSortBy) => {
    setCurrentPage(1);
    if (sortBy !== newSortBy) { setSortBy(newSortBy); setAscending(true); }
    else setAscending((p) => !p);

    // Claims are enriched fields; in this data set they only exist on Delivered/Accepted.
    // Default status "New" has none, so Claims sort looks broken. Widen to All Statuses.
    const isClaimSort = newSortBy === "hasClaims" || newSortBy === "primaryClaimStatus";
    if (isClaimSort && filter && String(filter).toLowerCase() === "new") {
      dispatch(setStatusFilter(null));
      dispatch(setListViewState({ stateKey, filter: null }));
      notify.info({
        title: "Claim sort",
        message: "Switched to All Statuses — New has no claimed shipments to sort.",
      });
    }


    markDirty();
  };

  const toggleChildShipments = useCallback(async (shipment) => {
    const parentNumber = shipment?.shipmentNumber;
    if (!parentNumber) return;

    const isExpanded = expandedParents.has(parentNumber);
    if (isExpanded) {
      setExpandedParents((previous) => {
        const next = new Set(previous);
        next.delete(parentNumber);
        return next;
      });
      return;
    }

    setExpandedParents((previous) => new Set(previous).add(parentNumber));
    if (childrenByParent[parentNumber] || loadingChildren.has(parentNumber)) return;

    setLoadingChildren((previous) => new Set(previous).add(parentNumber));
    try {
      const { data } = await httpClient.get(
        `/api/shipments/getChildShipments/${encodeURIComponent(parentNumber)}`,
        { headers: { "Office-Code": shipment.officeCode || officeCode } }
      );
      const legNum = (s) => { const m = String(s?.shipmentNumber || "").match(/-(\d+)$/); return m ? parseInt(m[1], 10) : 0; };
      setChildrenByParent((previous) => ({ ...previous, [parentNumber]: Array.isArray(data) ? [...data].sort((a, b) => legNum(a) - legNum(b)) : [] }));
    } catch (error) {
      if (error?.response?.status !== 404) {
        notify.error({ title: "Unable to load shipment legs", message: "Please try again." });
      }
      setChildrenByParent((previous) => ({ ...previous, [parentNumber]: [] }));
    } finally {
      setLoadingChildren((previous) => {
        const next = new Set(previous);
        next.delete(parentNumber);
        return next;
      });
    }
  }, [childrenByParent, expandedParents, loadingChildren, officeCode]);

  // ── Row formatting ──
  // The search endpoint identifies parents via shipmentType. Child legs are fetched only when
  // their parent is expanded, so normal shipments retain the existing one-row behavior.
  const isCarrier = React.useMemo(() => {
    return isCarrierOrAgentOrg();
  }, []);

  const displayedShipments = React.useMemo(() => {
    return shipments
      .filter((shipment) => isCarrier || shipment?.shipmentType !== "Leg")
      .flatMap((shipment) => {
        const parentNumber = shipment?.shipmentNumber;
        const hasChildren = shipment?.shipmentType === "Multi-leg parent";
        const parentRow = { ...shipment, __hasChildShipments: hasChildren };
        if (!hasChildren || !expandedParents.has(parentNumber)) return [parentRow];

        const children = childrenByParent[parentNumber] || [];
        return [
          parentRow,
          ...children.map((child) => ({
            ...child,
            __isChildShipment: true,
            __parentShipmentNumber: parentNumber,
          })),
        ];
      });
  }, [shipments, expandedParents, childrenByParent, isCarrier]);

  const formattedShipments = React.useMemo(
    () => displayedShipments.map((s) => formatShipmentRow(s, { isCarrier })),
    [displayedShipments, isCarrier]
  );

  // ── Selection ──
  const handleSelectAll = (checked) => {
    if (!checked) { setSelectedShipments([]); return; }
    setSelectedShipments(
      formattedShipments
        .map((r) => makeKey(r["Shipment Number"], r["Office Code"]))
        .filter((key) => !disabledByShipment.get(key))
    );
  };

  const handleSingleCheckbox = (key) => {
    if (disabledByShipment.get(key)) return;
    setSelectedShipments((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };

  // ── Navigation ──
  const handleViewShipment = (shipmentNumber, shipmentOfficeCode) => {
    dispatch(addTab({
      id: `view-${shipmentNumber}-${Date.now()}`,
      name: `${shipmentNumber}`,
      contentType: "shipmentView",
      shipmentNumber,
      shipmentOfficeCode,
      isDynamic: true,
      createdAt: Date.now(),
    }));
  };

  const handleEditShipment   = (id)        => {};
  const deleteShipment       = async (id)  => {};
  // ── Bulk: Create Invoice ──
  const handleBulkCreateInvoice = async () => {
    if (!selectedShipments.length) {
      notify.error({ title: "No Shipments Selected", message: "Please select at least one shipment." });
      return;
    }

    const chosen = shipments.filter((s) =>
      selectedShipments.includes(makeKey(s.shipmentNumber, s.officeCode))
    );
    if (!chosen.length) {
      notify.error({
        title: "Selection Out of Date",
        message: "The selected shipment(s) could not be matched to the current list. Please reselect and try again.",
      });
      return;
    }

    try {
      const details = await Promise.all(
        chosen.map((s) =>
          httpClient
            .get(`/api/shipments/invoice/${s.shipmentNumber}`, {
              headers: { Authorization: `Bearer ${token}`, "Office-Code": s.officeCode || officeCode },
            })
            .then((r) => r.data)
        )
      );

      const notCompleted = details.filter(
        (s) => !["completed", "delivered"].includes((s.status || "").toLowerCase())
      );
      if (notCompleted.length) {
        notify.error({
          title: "Not Ready to Invoice",
          message: `These shipment(s) are not Delivered: ${notCompleted.map((s) => s.shipmentNumber).join(", ")}`,
        });
        return;
      }
      const already = details.filter((s) => s.isInvoiced);
      if (already.length) {
        notify.error({
          title: "Already Invoiced",
          message: `Cannot invoice—already invoiced: ${already.map((s) => s.shipmentNumber).join(", ")}`,
        });
        return;
      }
      const collect = details.filter((s) => s.paymentMethod === "COLLECT");
      if (collect.length) {
        notify.error({
          title: "Collect Shipment",
          message: `Cannot invoice COLLECT shipment(s): ${collect.map((s) => s.shipmentNumber).join(", ")}`,
        });
        return;
      }

      const missingCarrier = details.filter((s) => !s?.carrierOfficeCode?.partyOfficeCode);
      if (missingCarrier.length) {
        notify.error({
          title: "No Carrier Assigned",
          message: `${missingCarrier.map((s) => s.shipmentNumber).join(", ")} ${missingCarrier.length === 1 ? "has" : "have"} no carrier assigned and cannot be invoiced.`,
        });
        return;
      }

      const billTo = details[0].officeCode;
      const payTo  = details[0].carrierOfficeCode.partyOfficeCode;
      if (details.some((s) => s.officeCode !== billTo || s.carrierOfficeCode?.partyOfficeCode !== payTo)) {
        notify.error({
          title: "Mismatched Accounts",
          message: "All shipments must share the same Bill-To and Pay-To accounts.",
        });
        return;
      }

      const shipperCode = details[0].shipperOfficeCode?.partyOfficeCode ?? null;
      if (details.some((s) => (s.shipperOfficeCode?.partyOfficeCode ?? null) !== shipperCode)) {
        const shippers = [
          ...new Set(
            details
              .map((s) => s.shipperOfficeCode?.name || s.shipperOfficeCode?.partyOfficeCode)
              .filter(Boolean)
          ),
        ];
        notify.error({
          title: "Mixed Shippers",
          message: `Selected shipments belong to different shippers. Create a separate invoice for each shipper.${
            shippers.length > 1 ? ` Shippers: ${shippers.join(", ")}.` : ""
          }`,
        });
        return;
      }

      const prefilled = details.map((d) => {
        const shipTosFromStops = (d.shipmentStops || [])
          .filter((s) => (s.stopType || "").toUpperCase() === "DROP_OFF")
          .sort((a, b) => (a.stopSequence ?? Number.MAX_SAFE_INTEGER) - (b.stopSequence ?? Number.MAX_SAFE_INTEGER))
          .map((s) => ({
            companyName: s.stopName ?? s.warehouseName ?? null,
            address:     s.addressLine1 ?? null,
            address2:    s.addressLine2 ?? null,
            city:        s.city ?? null,
            state:       s.state ?? null,
            zip:         s.zipCode ?? null,
            country:     s.country ?? null,
            phone:       s.contactPhone ?? null,
            email:       s.contactEmail ?? null,
            fax:         s.contactFax ?? null,
            stopNumber:  s.stopNumber ?? null,
            stopSequence:s.stopSequence ?? null,
            stopType:    s.stopType ?? null,
          }));

        const shipTos = shipTosFromStops.length
          ? shipTosFromStops
          : [{
              companyName: d.receiverOfficeCode?.name ?? d.receiverName ?? null,
              address:     d.receiverOfficeCode?.addressLine1 ?? null,
              city:        d.receiverOfficeCode?.city ?? null,
              state:       d.receiverOfficeCode?.state ?? null,
              zip:         d.receiverOfficeCode?.zipCode ?? null,
              country:     d.receiverOfficeCode?.country ?? null,
              phone:       d.receiverOfficeCode?.contactPhone ?? null,
              email:       d.receiverOfficeCode?.contactEmail ?? null,
              fax:         d.receiverOfficeCode?.contactFax ?? null,
            }];

        return {
          shipmentNumber:    d.shipmentNumber,
          shipmentOfficeCode:d.officeCode,
          shipmentOrigin:    d.portOfOrigin,
          shipmentDestination:d.portOfDestination,
          equipmentNumber:   d.isoEquipmentCode,
          shipmentCharge: (() => {
            const rev = (d.charges || [])
              .filter((c) => (c.type || "").toUpperCase() === "REVENUE")
              .reduce((sum, c) => sum + Number(c.amount ?? 0), 0);
            return rev > 0 ? rev : d.totalCharge;
          })(),
          charges: (() => {
            const all = d.charges || [];
            const revenue = all.filter((c) => (c.type || "").toUpperCase() === "REVENUE");
            const source = revenue.length > 0
              ? revenue
              : all.filter((c) => ["COST", "COST_PER_STOP"].includes((c.type || "").toUpperCase()));
            return source.map((c) => ({
              chargeCode: c.code, chargeAmount: c.amount, chargeCurrency: c.currency,
              chargeType: c.type, chargeAuditStatus: c.chargeApprovalStatus, isAutoPopulated: true,
            }));
          })(),
          estimatedCharges: (d.estimatedCharges || []).map((c) => ({
            chargeCode: c.code, chargeAmount: c.amount, chargeCurrency: c.currency,
            chargeType: c.type, chargeAuditStatus: c.chargeApprovalStatus, isAutoPopulated: true,
          })),
          billToCompanyName: fallbackName(d.shipperOfficeCode?.name),
          billToOfficeCode:  d.shipperOfficeCode?.partyOfficeCode,
          billToAddress:     d.shipperOfficeCode?.addressLine1,
          billToCity:        d.shipperOfficeCode?.city,
          billToState:       d.shipperOfficeCode?.state,
          billToZip:         d.shipperOfficeCode?.zipCode,
          billToCountry:     d.shipperOfficeCode?.country,
          billToPhone:       d.shipperOfficeCode?.contactPhone,
          billToEmail:       d.shipperOfficeCode?.contactEmail,
          billToFax:         d.shipperOfficeCode?.contactFax,
          payToCompanyName:  d.carrierName,
          payToOfficeCode:   d.carrierOfficeCode?.partyOfficeCode,
          payToAddress:      d.carrierOfficeCode?.addressLine1,
          payToCity:         d.carrierOfficeCode?.city,
          payToState:        d.carrierOfficeCode?.state,
          payToZip:          d.carrierOfficeCode?.zipCode,
          payToCountry:      d.carrierOfficeCode?.country,
          payToPhone:        d.carrierOfficeCode?.contactPhone,
          payToEmail:        d.carrierOfficeCode?.contactEmail,
          payToFax:          d.carrierOfficeCode?.contactFax,
          shipTos,
          shipToCompanyName: shipTos[0]?.companyName ?? null,
          shipToOfficeCode:  d.receiverOfficeCode?.partyOfficeCode ?? null,
          shipToAddress:     shipTos[0]?.address ?? null,
          shipToCity:        shipTos[0]?.city ?? null,
          shipToState:       shipTos[0]?.state ?? null,
          shipToZip:         shipTos[0]?.zip ?? null,
          shipToCountry:     shipTos[0]?.country ?? null,
          shipToPhone:       shipTos[0]?.phone ?? null,
          shipToEmail:       shipTos[0]?.email ?? null,
          shipToFax:         shipTos[0]?.fax ?? null,
        };
      });

      dispatch(addInvoiceTab({ id: `invoice-new-${Date.now()}`, name: "New Invoice", type: "new", data: prefilled }));
      dispatch(setActiveInvoiceTab({ index: defaultTabs.length + dynamicTabs.length }));
      navigate("/dashboard/invoices");
    } catch (err) {
      notify.error({
        title: "Failed to Create Invoice",
        message: err.response?.data?.content || err.message || "Unexpected error while fetching invoice data.",
      });
    }
  };

  // ── Bulk: Refresh Miles ──
  const handleBulkRefreshMiles = async () => {
    if (!selectedShipments.length) {
      notify.error({ title: "No Shipments Selected", message: "Please select at least one shipment." });
      setShowBulkMenu(false);
      return;
    }
    const shipmentNumbers = selectedShipments.map((k) => k.split("::")[0]);
    setIsRefreshingMiles(true);
    try {
      const { data } = await httpClient.post(
        `/api/shipments/refresh-transit-miles`,
        { shipmentNumbers },
        { headers: { Authorization: `Bearer ${token}`, "Office-Code": officeCode } }
      );
      const { totalFound, successfullyUpdated, failed, noChange, failures } = data;
      // One toast, styled to match what actually happened (see handleBulkRefreshRates for why).
      const summary = `Total: ${totalFound} | Updated: ${successfullyUpdated} | Unchanged: ${noChange} | Failed: ${failed}`;
      const details = (failures || []).map((f) => `${f.shipmentNumber} (${f.errorMessage})`).join("; ");
      if (failed === 0) {
        notify.success({ title: "Miles Refreshed", message: summary, options: { autoClose: 4000 } });
      } else if (successfullyUpdated === 0) {
        notify.error({ title: "Miles Refresh Failed", message: `${summary}\n${details}` });
      } else {
        notify.warning({ title: "Miles Partially Refreshed", message: `${summary}\n${details}` });
      }
      setSelectedShipments([]);
      fetchShipments();
    } catch (err) {
      notify.error({ title: "Refresh Failed", message: err?.response?.data?.message || "Could not refresh miles." });
    } finally {
      setIsRefreshingMiles(false);
      setShowBulkMenu(false);
    }
  };

  // ── Bulk: Refresh Rates ──
  // No bulk backend endpoint exists for this (unlike Refresh Miles) -- rate refresh has always
  // been orchestrated client-side per shipment (see rateRefresh.js, used by the single-shipment
  // "Refresh Rates" action in ShipmentDetailsView.js). This loops that same, already-proven
  // logic across the selection in "silent" mode (suppressing its per-shipment toasts) and
  // aggregates one summary toast, matching Refresh Miles' UX.
  const handleBulkRefreshRates = async () => {
    if (!selectedShipments.length) {
      notify.error({ title: "No Shipments Selected", message: "Please select at least one shipment." });
      setShowBulkMenu(false);
      return;
    }
    const shipmentEntries = selectedShipments.map((k) => {
      const [shipmentNumber, shipmentOfficeCode] = k.split("::");
      return { shipmentNumber, shipmentOfficeCode };
    });
    setIsRefreshingRates(true);
    const failures = [];
    const skipped = [];
    let successfullyUpdated = 0;
    try {
      for (const { shipmentNumber, shipmentOfficeCode } of shipmentEntries) {
        try {
          // detailed-shipment does an exact office-code match lookup (unlike Refresh Miles'
          // backend endpoint, which just looks up by shipment number) -- a shipper admin
          // managing multiple offices can have selected shipments owned by different offices in
          // the same grid view, so this must use each row's OWN office code, not the grid's
          // single active-office cookie.
          const { data: fullShipment } = await httpClient.get(
            `/api/shipments/detailed-shipment/${shipmentNumber}`,
            { headers: { Authorization: `Bearer ${token}`, "Office-Code": shipmentOfficeCode || officeCode } }
          );

          // Rate-shopping inherently needs an assigned carrier to rate-shop against, and only
          // ever applies to Prepaid shipments (Collect shipments are never invoiced -- see the
          // same rule enforced on invoice creation). Neither of these applies to Refresh Miles
          // (a pure stop-to-stop distance calc), so they're checked here only.
          const hasCarrier = Boolean(
            fullShipment?.carrierOfficeCode?.partyOfficeCode || fullShipment?.carrierName
          );
          const isPrepaid = (fullShipment?.paymentMethod || "").toUpperCase() === "PREPAID";

          if (isRefreshBlockedForShipment(fullShipment)) {
            skipped.push({ shipmentNumber, reason: "Delivered/Completed and already invoiced" });
            continue;
          }
          if (!hasCarrier) {
            skipped.push({ shipmentNumber, reason: "No carrier assigned" });
            continue;
          }
          if (!isPrepaid) {
            skipped.push({ shipmentNumber, reason: "Only Prepaid shipments can be refreshed" });
            continue;
          }

          const result = await refreshRatesAndUpdateCharges({
            shipmentDetails: fullShipment,
            officeCode: shipmentOfficeCode || officeCode,
            isRefreshingRates: false,
            setIsRefreshingRates: () => {},
            silent: true,
          });
          if (result?.success) {
            successfullyUpdated += 1;
          } else {
            failures.push({ shipmentNumber, reason: result?.reason || "Refresh failed" });
          }
        } catch (err) {
          failures.push({
            shipmentNumber,
            reason: err?.response?.data?.message || "Could not fetch shipment details.",
          });
        }
      }
      // One toast, styled to match what actually happened -- a green "success" toast when
      // everything failed/was skipped is misleading, and a second, separate failure toast on
      // top of it is redundant noise.
      const summary = `Total: ${shipmentEntries.length} | Updated: ${successfullyUpdated} | Skipped: ${skipped.length} | Failed: ${failures.length}`;
      const details = [...skipped, ...failures]
        .map((s) => `${s.shipmentNumber} (${s.reason})`)
        .join("; ");
      if (skipped.length === 0 && failures.length === 0) {
        notify.success({ title: "Rates Refreshed", message: summary, options: { autoClose: 4000 } });
      } else if (successfullyUpdated === 0) {
        notify.error({ title: "Rates Refresh Failed", message: `${summary}\n${details}` });
      } else {
        notify.warning({ title: "Rates Partially Refreshed", message: `${summary}\n${details}` });
      }
      setSelectedShipments([]);
      fetchShipments();
    } finally {
      setIsRefreshingRates(false);
      setShowBulkMenu(false);
    }
  };

  // ── Action dropdown (portal-based to escape stacking contexts) ──
  const handleActionClick = (key, e) => {
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    setDropdownPos({ top: rect.bottom + window.scrollY, left: rect.left + window.scrollX });
    setOpenDropdownRow((prev) => (prev === key ? null : key));
  };

  const renderActionColumn = (row, disabled, getStickyCellProps) => {
    const key   = makeKey(row["Shipment Number"], row["Office Code"]);
    const id    = row["Shipment Number"];
    const shipOC= row["Office Code"];
    const isOpen= openDropdownRow === key;
    const { className: stickyClass, style: stickyStyle } = getStickyCellProps("Action");

    const actions = disabled
      ? [{ icon: "Eye",   label: "View",   color: "blue",  action: () => handleViewShipment(id, shipOC) }]
      : [
          { icon: "Eye",   label: "View",   color: "blue",  action: () => handleViewShipment(id, shipOC) },
          { icon: "Edit",  label: "Edit",   color: "green", action: () => handleEditShipment(id, shipOC) },
          { icon: "Trash", label: "Delete", color: "red",   action: () => deleteShipment(id, shipOC) },
        ];

    return (
      <>
        <td
          onClick={(e) => e.stopPropagation()}
          className={`bg-white dark:bg-acs-dark-surface border-b border-gray-200 dark:border-acs-dark-border text-center transition-none z-[10] ${stickyClass}`}
          style={{
            ...stickyStyle,
            width: `${ACTION_COLUMN_WIDTH}px`, minWidth: `${ACTION_COLUMN_WIDTH}px`, maxWidth: `${ACTION_COLUMN_WIDTH}px`,
          }}
        >
          <button
            data-action-trigger
            className={`p-1 relative w-full transition-none ${
              disabled
                ? "opacity-60 hover:opacity-80"
                : "text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-acs-dark-text"
            }`}
            onClick={(e) => handleActionClick(key, e)}
          >
            <MoreHorizontal size={18} className="mx-auto" />
          </button>
        </td>

        {isOpen && ReactDOM.createPortal(
          <div
            ref={dropdownRef}
            style={{ position: "absolute", top: dropdownPos.top, left: dropdownPos.left, zIndex: 9999 }}
          >
            <ActionMenu
              rowId={key} isOpen onClose={() => setOpenDropdownRow(null)}
              moduleName="Shipment" actions={actions}
            />
          </div>,
          document.body
        )}
      </>
    );
  };

  // ─── Render ────────────────────────────────────────────────────────────────

  // Views + Tabs, shared by both toolbar layouts. Wrapped in its own flex row so
  // the toolbar's justify-between doesn't push the two controls apart.
  const viewControls = !isSearchTab && (
    <div className="flex items-center gap-2">
      <TableSavedViewsControl
        controlLabel={controlLabel}
        views={views}
        activeView={activeView}
        loading={viewsLoading}
        error={viewsError}
        isDirty={isDirty}
        allDataHeaders={TABLE_HEADERS.slice(2)}
        dataHeaders={dataHeaders}
        onSelectView={selectView}
        onSaveNew={saveNewView}
        onUpdateActive={updateActiveView}
        onRename={renameView}
        onDelete={deleteView}
        onSetDefault={setDefaultView}
        onUpdateColumnVisibility={updateColumnVisibility}
        onManageColumns={onManageColumns}
      />
      {viewTabs && (
        <ShipmentViewTabsControl
          viewTabs={viewTabs.viewTabs}
          loading={viewTabs.loading}
          error={viewTabs.error}
          currentViewTabId={viewTab?.id ?? null}
          views={views}
          activeView={activeView}
          captureConfig={captureConfig}
          onCreate={viewTabs.addViewTab}
          onRename={viewTabs.renameViewTab}
          onDelete={viewTabs.removeViewTab}
          onUpdateConfig={viewTabs.updateViewTabConfig}
          onMove={viewTabs.moveViewTab}
        />
      )}
    </div>
  );

  return (
    <div className="bg-white dark:bg-acs-dark-surface flex flex-col h-full transition-none">

      {/* Advanced filter toolbar (button + pills) */}
      {showAdvancedFilters ? (
        <AdvancedFilterToolbar
          filters={advancedFilters}
          activeCount={activeCount}
          onOpenModal={() => setShowFilterModal(true)}
          onClearAll={() => { clearFilters(); setCurrentPage(1); markDirty(); }}
          onRemovePill={(key) => { removePill(key); setCurrentPage(1); markDirty(); }}
          trailing={viewControls}
        />
      ) : (
        viewControls && (
          <div className="flex items-center justify-end gap-2 border-b border-gray-200 px-3 py-2 dark:border-acs-dark-border">
            {viewControls}
          </div>
        )
      )}

      {/* Table */}
      <div
        ref={tableContainerRef}
        className="flex-1 min-h-[200px] sm:min-h-[300px] overflow-x-auto bg-white dark:bg-acs-dark-surface transition-none relative"
      >
        <Table
          {...columnManagerProps}
          data={formattedShipments}
          initialColumnWidths={savedColumnWidths}
          onColumnResize={onColumnResize}
          onSelectAll={handleSelectAll}
          renderCheckboxActions={() => setShowBulkMenu((p) => !p)}
          checkboxHeaderRef={checkboxHeaderRef}
          selectedRows={selectedShipments}
          totalValidRows={formattedShipments.filter(
            (r) => !disabledByShipment.get(makeKey(r["Shipment Number"], r["Office Code"]))
          ).length}
          autoGrowColumns
          dataColumnMinWidth={DATA_COLUMN_MIN_WIDTH}
          dataColumnMaxWidth={DATA_COLUMN_MAX_WIDTH}
          enableColumnResize
          enableColumnReorder
          enableColumnPin
          nonHideableColumnIds={[SHIPMENT_NUMBER_HEADER]}
          sortBy={sortBy}
          ascending={ascending}
          onSort={(h) => { const k = SORTABLE_COLUMNS[h]; if (k) handleSort(k); }}
          sortableHeaders={Object.keys(SORTABLE_COLUMNS)}
          sortKeyByHeader={(h) => SORTABLE_COLUMNS[h]}
        >
          {(row, _rowIdx, columnWidths, getStickyCellProps, visibleColumns) => {
            const sn      = row["Shipment Number"];
            const key     = makeKey(sn, row["Office Code"]);
            const disabled= disabledByShipment.get(key);
            const { className: checkboxClass, style: checkboxStyle } = getStickyCellProps("Selection");
            const orderedDataHeaders = (visibleColumns ?? [])
              .map((col) => col.id)
              .filter((id) => id !== "Selection" && id !== "Action");
            const dataHeadersToRender = orderedDataHeaders.length ? orderedDataHeaders : dataHeaders;
            const isChildShipment = row.__isChildShipment;
            const hasChildShipments = row.__hasChildShipments;
            const isParentExpanded = expandedParents.has(sn);
            const isChildrenLoading = loadingChildren.has(sn);
            const legCount = hasChildShipments ? (childrenByParent[sn]?.length || 0) : 0;
            const childLegNumber = isChildShipment
              ? (String(sn).match(/-(\d+)$/)?.[1] || null)
              : null;

            return (
              <>
                {/* Selection */}
                <td
                  className={`bg-white dark:bg-acs-dark-surface border-b border-gray-200 dark:border-acs-dark-border z-[10] transition-none ${checkboxClass}`}
                  style={{
                    ...checkboxStyle,
                    width: `${SELECTION_COLUMN_WIDTH}px`, minWidth: `${SELECTION_COLUMN_WIDTH}px`, maxWidth: `${SELECTION_COLUMN_WIDTH}px`,
                  }}
                >
                  <input
                    type="checkbox"
                    className="relative appearance-none cursor-pointer mx-auto block w-[15px] h-[15px] rounded-sm border border-gray-400 dark:border-acs-dark-border bg-white dark:bg-acs-dark-surface transition-none checked:bg-acs-checkbox dark:checked:bg-acs-checkbox-dark checked:border-acs-checkbox after:content-[''] after:absolute after:inset-0 after:flex after:items-center after:justify-center checked:after:content-['✓'] checked:after:text-white checked:after:text-[12px] checked:after:font-bold"
                    checked={selectedShipments.includes(key)}
                    disabled={disabled}
                    onChange={() => handleSingleCheckbox(key)}
                  />
                </td>

                {/* Action (portal dropdown) */}
                {renderActionColumn(row, disabled, getStickyCellProps)}

                {/* Data columns */}
                {dataHeadersToRender.map((header) => {
                  const isExpanded       = expandedRowKey === key;
                  const { className: colClass, style: colStyle } = getStickyCellProps(header);

                  return (
                    <td
                      key={header}
                      className={`px-2 sm:px-3 py-1 sm:py-2 text-gray-900 dark:text-acs-dark-text text-xs sm:text-sm md:text-base border-b border-gray-200 dark:border-acs-dark-border font-roboto transition-none ${
                        isChildShipment ? "bg-[#faf9ff]" : ""
                      } ${colClass}`}
                      onDoubleClick={() => handleViewShipment(sn, row["Office Code"])}
                      {...getRowHoverProps(key)}
                      style={{
                        ...colStyle,
                        whiteSpace:    isExpanded ? "normal"   : "nowrap",
                        overflow:      "hidden",
                        textOverflow:  "ellipsis",
                        overflowWrap:  "break-word",
                        wordBreak:     "break-word",

                        minWidth:      `${columnWidths?.[header] || DATA_COLUMN_MIN_WIDTH}px`,
                        maxWidth:      isExpanded ? `${DATA_COLUMN_MAX_WIDTH * 2}px` : `${DATA_COLUMN_MAX_WIDTH}px`,
                        width:         columnWidths?.[header] ? `${columnWidths[header]}px` : `${DATA_COLUMN_MIN_WIDTH}px`,
                        transition:    "max-width 120ms ease",
                      }}
                    >
                      {header === SHIPMENT_NUMBER_HEADER ? (
                        <div className={`flex min-w-0 flex-nowrap items-center gap-2 ${isChildShipment ? "pl-4 text-slate-600 dark:text-slate-300" : ""}`}>
                          {hasChildShipments ? (
                            <button
                              type="button"
                              onClick={(event) => {
                                event.stopPropagation();
                                toggleChildShipments({ shipmentNumber: sn, officeCode: row["Office Code"] });
                              }}
                              className={`group flex h-6 w-6 shrink-0 items-center justify-center rounded-md border transition-colors ${
                                isParentExpanded
                                  ? "border-[#223688] bg-[#223688] text-white"
                                  : "border-[#c7d0f5] bg-white text-[#223688] hover:border-[#223688] hover:bg-[#eef2ff] dark:border-acs-dark-border dark:bg-acs-dark-surface"
                              }`}
                              aria-label={`${isParentExpanded ? "Collapse" : "Show"} shipment legs for ${sn}`}
                              aria-expanded={isParentExpanded}
                            >
                              {isChildrenLoading ? (
                                <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-current border-t-transparent" />
                              ) : isParentExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                            </button>
                          ) : isChildShipment ? (
                            <span className="relative flex h-6 w-6 shrink-0 items-center justify-center" aria-hidden="true">
                              <span className="absolute left-2.5 top-0 h-1/2 w-px bg-[#c7d0f5] dark:bg-slate-600" />
                              <span className="absolute left-2.5 top-1/2 h-px w-2.5 bg-[#c7d0f5] dark:bg-slate-600" />
                              <span className="absolute left-[1.05rem] top-1/2 h-1.5 w-1.5 -translate-y-1/2 rounded-full border border-[#8fa1e8] bg-white dark:bg-acs-dark-surface" />
                            </span>
                          ) : (
                            <span className="w-6 shrink-0" />
                          )}
                          {isChildShipment && childLegNumber ? (
                            <span
                              title={`Leg ${childLegNumber}`}
                              className="inline-flex shrink-0 items-center rounded-md bg-[#eef2ff] px-1.5 py-0.5 text-[10px] font-bold uppercase leading-none tracking-wide text-[#223688] dark:bg-blue-900/40 dark:text-blue-200"
                            >
                              Leg {childLegNumber}
                            </span>
                          ) : null}
                          <span
                            title={row[header]}
                            className={`min-w-0 truncate ${hasChildShipments ? "font-semibold text-[#223688] dark:text-blue-200" : ""}`}
                          >
                            {row[header]}
                          </span>
                          {hasChildShipments ? (
                            <span
                              title={legCount ? `Multi-leg shipment · ${legCount} legs` : "Multi-leg shipment"}
                              className="inline-flex shrink-0 items-center gap-1 rounded-full bg-[#223688] px-2 py-0.5 text-[11px] font-semibold text-white shadow-sm"
                            >
                              <GitBranch size={12} aria-hidden="true" />
                              {legCount ? `${legCount} legs` : "Multi-leg"}
                            </span>
                          ) : null}
                        </div>
                      ) : row[header]}
                    </td>
                  );
                })}
              </>
            );
          }}
        </Table>

        {/* Bulk action menu */}
        {showBulkMenu &&
          (() => {
            const bulkMenuStyle = getBulkMenuStyle();
            if (!bulkMenuStyle) return null;

            return (
              <div
                ref={bulkMenuRef}
                className="absolute z-[1000]"
                style={bulkMenuStyle}
              >
                <ActionMenu
                  rowId="bulk"
                  isOpen
                  onClose={() => setShowBulkMenu(false)}
                  moduleName="Shipment"
                  placement="header"
                  actions={[
                    ...(canCreateInvoice
                      ? [
                          {
                            icon: "FilePlus",
                            label: "Create Invoice",
                            color: "blue",
                            action: async () => {
                              await handleBulkCreateInvoice();
                              setShowBulkMenu(false);
                            },
                          },
                        ]
                      : []),
                    ...(canRefreshMilesOrRates
                      ? [
                          {
                            icon: "RefreshCw",
                            label: isRefreshingMiles ? "Refreshing..." : "Refresh Miles",
                            color: "green",
                            action: handleBulkRefreshMiles,
                          },
                          {
                            icon: "RefreshCw",
                            label: isRefreshingRates ? "Refreshing..." : "Refresh Rates",
                            color: "green",
                            action: handleBulkRefreshRates,
                          },
                        ]
                      : []),
                  ]}
                />
              </div>
            );
          })()}

        {/* Loading overlay */}
        {showLoadingOverlay && (
          <div className="absolute inset-0 z-[2000] flex items-center justify-center bg-white/60 dark:bg-black/30">
            <Loader color="text-blue-600 dark:text-acs-dark-text" size="h-5 w-5" message="Loading Shipments..." />
          </div>
        )}
      </div>

      <GlobalPagination
        label="Shipments per page"
        currentPage={currentPage}
        totalPages={totalPages}
        totalItems={totalItems}

        onPageChange={setCurrentPage}
        itemsPerPage={shipmentsPerPage}
        onItemsPerPageChange={setShipmentsPerPage}
      />

      {/* Advanced filter modal (rule-based: field + operator + value, joined by and/or) */}
      {showFilterModal && (
        <AdvancedFilterModal
          filters={advancedFilters}
          quickFilterStatus={filter}
          statusOptions={advancedFilterStatusOptions}
          onApply={(f) => {
            updateFilters(f);
            // A status rule owns the status constraint — drop the quick filter.
            if (filtersOverrideStatus(f)) {
              dispatch(setStatusFilter(null));
              dispatch(setListViewState({ stateKey, filter: null }));
            }
            setCurrentPage(1);
            markDirty();
          }}
          onClose={() => setShowFilterModal(false)}
        />
      )}
    </div>
  );
};

export default ShipmentsTable;